import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useRef, useCallback, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";
import type { Permission } from "../../auth/HasPermission";
import { logger } from "../../observability/LoggerFactory";
import { ROLE_LIST_WINDOW_QUERY } from "../../api/RoleListWindowQuery";

type ListRolesResponse =
  paths["/roles"]["get"]["responses"]["200"]["content"]["application/json"];

export type RoleListItem = ListRolesResponse["items"][number];

// Состояние экрана держит пермишены роли под общим алиасом Permission (один источник для всех экранов).
type RoleRow = Omit<RoleListItem, "permissions"> & { permissions: Permission[] };

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: RoleRow[] }
  | { kind: "error"; message: string };

export interface UseRoleListResult {
  state: ScreenState;
  fetching: boolean;
  refetch: () => void;
}

export function useRoleList(): UseRoleListResult {
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadDone = useRef(false);

  const fetchRoles = useCallback(async () => {
    if (abortRef.current !== null) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    if (!initialLoadDone.current) {
      setState({ kind: "loading" });
    }
    setFetching(true);

    try {
      const response = await fetchWithRefresh(`/api/roles?${ROLE_LIST_WINDOW_QUERY}`, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = (await response.json()) as ListRolesResponse;
      initialLoadDone.current = true;
      if (data.items.length === 0) {
        setState({ kind: "empty" });
      } else {
        setState({ kind: "data", items: data.items });
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      logger.error({ err: message }, "Failed to fetch roles");
      setState({ kind: "error", message });
    } finally {
      if (!controller.signal.aborted) {
        setFetching(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchRoles();
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
    };
  }, [fetchRoles]);

  const refetch = useCallback(() => {
    void fetchRoles();
  }, [fetchRoles]);

  return { state, fetching, refetch };
}
